import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageShell } from '@/components/Layout';
import { Device } from '@/lib/device';
import { fetchDbObject } from '@/api/endpoints';
import { DetailTable } from '@/components/db/DetailTable';
import { DetailHypertable } from '@/components/db/DetailHypertable';
import { DetailCagg } from '@/components/db/DetailCagg';
import type { DbObjectDetail } from '@/api/types';

// Map the enriched API detail to the component shapes — all live from the DB.
function toTableObj(o: DbObjectDetail) {
  return {
    schema: o.schema,
    name: o.name,
    type: 'table' as const,
    on_disk_bytes: o.total_bytes,
    heap_bytes: o.heap_bytes,
    index_bytes: o.index_bytes,
    rows: o.row_estimate ?? 0,
  };
}

function toHypertableObj(o: DbObjectDetail) {
  const decompressed = o.chunks.reduce((s, c) => s + c.decompressed_bytes, 0) || o.total_bytes;
  return {
    schema: o.schema,
    name: o.name,
    type: 'hypertable' as const,
    on_disk_bytes: o.total_bytes,
    decompressed_bytes: decompressed,
    compression_pct: o.compression_pct,
    rows: o.row_estimate ?? 0,
    chunks_count: o.chunk_count ?? o.chunks.length,
    chunk_interval: o.chunk_interval ?? '—',
    policies: o.policies,
  };
}

function toCaggObj(o: DbObjectDetail) {
  const decompressed = o.chunks.reduce((s, c) => s + c.decompressed_bytes, 0) || o.total_bytes;
  return {
    schema: o.schema,
    name: o.name,
    type: 'cagg' as const,
    source_hypertable: o.source_hypertable ?? '—',
    on_disk_bytes: o.total_bytes,
    decompressed_bytes: decompressed,
    compression_pct: o.compression_pct,
    rows: o.row_estimate ?? 0,
    chunks_count: o.chunk_count ?? o.chunks.length,
    chunk_interval: o.chunk_interval ?? '—',
    materialized_only: o.materialized_only,
    policies: o.policies,
  };
}

export default function DbDetail({ device }: { device: Device }) {
  const { schema, name } = useParams<{ schema: string; name: string }>();

  const { data: object, isLoading, isError } = useQuery({
    queryKey: ['db', 'object', schema, name],
    queryFn: () => fetchDbObject(schema!, name!),
    enabled: !!schema && !!name,
  });

  return (
    <PageShell device={device} title="Détail objet" breadcrumb="Base de données">
      <div className="p-4 md:p-6">
        {isLoading && (
          <div className="p-8 text-muted-foreground">Chargement…</div>
        )}

        {isError && (
          <div className="bg-card border border-border rounded-lg p-6 text-sm">
            <p className="font-semibold mb-2">Objet introuvable</p>
            <p className="text-muted-foreground mb-3">
              <code className="font-mono">{schema}.{name}</code> n'existe pas en base.
            </p>
            <Link to="/db" className="text-primary hover:underline text-sm">
              ← Retour à la liste
            </Link>
          </div>
        )}

        {object && object.type === 'table' && (
          <DetailTable obj={toTableObj(object)} />
        )}

        {object && object.type === 'hypertable' && (
          <DetailHypertable obj={toHypertableObj(object)} chunks={object.chunks} />
        )}

        {object && object.type === 'cagg' && (
          <DetailCagg obj={toCaggObj(object)} chunks={object.chunks} />
        )}
      </div>
    </PageShell>
  );
}
